import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CircleNotch, ArrowSquareOut, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currencySymbols = { USD: '$', GBP: '\u00A3', AUD: 'A$' };

export default function StorePage() {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/stores/${storeId}/products`)
      .then(({ data }) => {
        setStore(data.store);
        setProducts(data.products);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [storeId]);

  const categories = ['all', ...new Set(products.map(p => p.category))].sort();
  const categoryLabels = { all: 'All', sports: 'Sports', football: 'Football / Rugby / Soccer', boots: 'Boots', outdoor: 'Outdoor', womens_fashion: "Women's Fashion", mens_fashion: "Men's Fashion", sandals: "Sandals & Flip Flops", kids: 'Kids' };

  const filtered = categoryFilter === 'all'
    ? products
    : products.filter(p => p.category === categoryFilter);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>;
  if (!store) return <div className="min-h-screen flex items-center justify-center"><p>Store not found</p></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="store-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <Link to="/browse" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] mb-6" data-testid="back-to-browse">
          <ArrowLeft size={16} /> Back to Store Directory
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-1">{store.region} Retailer</p>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase" data-testid="store-name">{store.name}</h1>
            <p className="text-sm text-[#52525B] mt-1">{products.length} products available</p>
          </div>
          {store.website_url && (
            <a href={store.website_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-[#0A0A0A] hover:text-white transition-colors"
              data-testid="visit-store-btn">
              Visit Store <ArrowSquareOut size={14} />
            </a>
          )}
        </div>

        {/* Category filter */}
        <div className="flex gap-2 flex-wrap mb-8" data-testid="store-category-filter">
          {categories.map(c => (
            <button key={c} onClick={() => setCategoryFilter(c)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${categoryFilter === c ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
              data-testid={`store-cat-filter-${c}`}>
              {categoryLabels[c] || c}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map((shoe, i) => (
              <motion.div key={shoe.shoe_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Link to={`/shoe/${shoe.shoe_id}`} className="block border border-[#E4E4E7] bg-white hover:border-[#0A0A0A] transition-all group" data-testid={`store-product-${shoe.shoe_id}`}>
                  <div className="overflow-hidden">
                    <img src={shoe.image_url} alt={shoe.model} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.target.src = `https://placehold.co/400x400/F4F4F5/52525B?text=${encodeURIComponent(shoe.brand)}`; }} />
                  </div>
                  <div className="p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
                    <h3 className="font-heading font-bold text-sm mt-1">{shoe.model}</h3>
                    <p className="text-xs text-[#52525B] capitalize mt-1">{shoe.category}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="font-bold text-sm">
                        {currencySymbols[shoe.store_currency] || '$'}{shoe.store_price?.toFixed(2)}
                      </p>
                      <span className="text-xs">
                        {shoe.in_stock ? <span className="text-[#10B981] flex items-center gap-1"><Check size={12} /> In Stock</span> : <span className="text-[#F59E0B]">Low Stock</span>}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-[#E4E4E7]">
            <p className="text-[#52525B] text-lg">No products found for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
}
